import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App.tsx";
import { LangProvider } from "./lib/i18n.tsx";
import { completeSignIn, isSignedIn, signIn } from "./lib/auth.ts";

// Tokens, theme and base styles (light by default; see lib/themeMode.ts).
import "./styles.css";
// Daily-work pages (Home, To order, Arriving, Search, detail panels).
import "./ui.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Everything is served from D1 — 30s stale is plenty and keeps
      // the dashboard snappy when you flip tabs.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root")!);

/** Plain page for the moments before the app can render. */
function Notice({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div style={{ maxWidth: 480, margin: "15vh auto", padding: "0 16px", color: "var(--text)", lineHeight: 1.5 }}>
      <h2 style={{ fontWeight: 600 }}>{title}</h2>
      {children}
    </div>
  );
}

function renderApp() {
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <LangProvider>
            <App />
          </LangProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

// Sign-in gate (lib/auth.ts): finish a sign-in (Microsoft or email + password) on /auth/callback,
// send a signed-out visitor to sign in, otherwise render the app.
async function boot() {
  if (location.pathname === "/auth/callback") {
    root.render(<Notice title="Signing you in… · 正在登录…" />);
    try {
      const back = await completeSignIn();
      history.replaceState(null, "", back);
    } catch (e) {
      root.render(
        <Notice title="Sign-in didn't complete · 登录未完成">
          <p>{(e as Error).message}</p>
          <button onClick={() => void signIn()} style={{ font: "inherit", padding: "8px 18px", cursor: "pointer" }}>
            Sign in · 登录
          </button>
        </Notice>,
      );
      return;
    }
  } else if (location.pathname === "/signed-out") {
    root.render(
      <Notice title="You're signed out · 您已退出登录">
        <button onClick={() => { history.replaceState(null, "", "/"); void signIn(); }} style={{ font: "inherit", padding: "8px 18px", cursor: "pointer" }}>
          Sign in · 登录
        </button>
      </Notice>,
    );
    return;
  } else if (!isSignedIn()) {
    root.render(<Notice title="Opening sign-in… · 正在打开登录页…" />);
    await signIn();
    return;
  }
  renderApp();
}
void boot();
